// Aging (Domain/Money; was HomeViews.tsx's, HIERARCHY follow-up 8, BL-189): who owes what
// (receivables.aging): what's owed as a figure and how much of it is late; in the office each bucket a
// RowList row with its bar, every bar on one track so one compares with the next (late hatched, over 30
// days red); then the latest late invoices as RowList rows, the most overdue first (three, two in a chat),
// the amount red past 30 days. A page that lists every open invoice under it (Accounting › Money owed)
// leaves the late ones out (`late={false}`). In a chat, a way into Flowology. A report can give its own
// ages in place of the five (`ages`, opt-in: Reports › AR aging, by invoice date or by due date), each
// with its count; only those past their due date are hatched and counted late. Reads only --aging-*;
// Figure, RowList and Button read their own.
import { AGING_BUCKETS, type AgingProps } from '../../schemas.ts';
import type { ViewProps } from '../../host.ts';
import { money, plural } from '../../format.ts';
import { Figure } from '../Figures/Figures.tsx';
import { RowList } from '../RowList/RowList.tsx';
import { ViewAct } from '../view-act.tsx';
import './Aging.css';

const BUCKET_WORDS: Record<(typeof AGING_BUCKETS)[number], string> = {
  current: 'Not due yet',
  days_1_30: '1 to 30 days late',
  days_31_60: '31 to 60 days late',
  days_61_90: '61 to 90 days late',
  days_over_90: 'Over 90 days',
};

/** One of a report's own ages (Reports › AR aging's "31–60"), in place of receivables.aging's five. */
export interface AgingAge {
  id: string;
  /** "31–60", "Not yet due", "Undated". */
  name: string;
  cents: number;
  /** How many invoices, under its amount ("9 invoices"). */
  count?: number;
  /** Past its due date: hatched, and counted in the figure's late line. */
  late?: boolean;
  /** Red: over 30 days past due. */
  bad?: boolean;
}

/** receivables.aging's five ages, as `ages` says a report's own. */
const bucketAges = (a: AgingProps): AgingAge[] =>
  AGING_BUCKETS.map((b) => ({
    id: b,
    name: BUCKET_WORDS[b],
    cents: a.buckets[b],
    late: b !== 'current',
    bad: b === 'days_31_60' || b === 'days_61_90' || b === 'days_over_90',
  }));

export interface AgingViewProps extends ViewProps<AgingProps> {
  /** The latest late invoices under the buckets (the default); false where the page lists them all. */
  late?: boolean;
  /** A report's own ages, in place of the five (opt-in). */
  ages?: readonly AgingAge[];
}

/**
 * receivables.aging. With `ages` (opt-in), a report's own ages draw in place of the five; where none can
 * be late (aged by invoice date), the figure has no late line.
 */
export function Aging({ data: a, host, late: showLate = true, ages }: AgingViewProps) {
  const chat = host.surface === 'chat';
  const late = a.rows.filter((r) => r.daysPastDue > 0).sort((x, y) => y.daysPastDue - x.daysPastDue);
  const shown = ages ?? bucketAges(a);
  const most = Math.max(1, ...shown.map((b) => b.cents));
  const lateCents = shown.filter((b) => b.late).reduce((s, b) => s + b.cents, 0);
  const sub = !shown.some((b) => b.late) ? null : lateCents ? `${money(lateCents)} late` : 'Nothing late';
  return (
    <article className="fl-aging" data-surface={host.surface} aria-label="Who owes you">
      <Figure label="Owed to you" value={money(a.openInvoicesCents)} sub={sub} />
      {!chat && (
        <div className="fl-aging__buckets">
          <RowList
            label="Owed by age"
            rows={shown.map((b) => ({
              id: b.id,
              name: b.name,
              ...(b.cents > 0
                ? {
                    bar: {
                      value: b.cents,
                      max: most,
                      label: b.name,
                      fill: b.late ? ('hatch' as const) : ('solid' as const),
                      state: b.bad ? ('bad' as const) : ('ok' as const),
                    },
                  }
                : {}),
              end: b.cents
                ? {
                    figure: money(b.cents),
                    ...(b.count != null ? { sub: plural(b.count, 'invoice') } : {}),
                  }
                : { aside: 'Nothing' },
            }))}
          />
        </div>
      )}
      {showLate && late.length > 0 && (
        <RowList
          label="Late invoices"
          rows={late.slice(0, chat ? 2 : 3).map((r) => ({
            id: r.invoiceId,
            name: r.customerName,
            lines: [
              { text: `${r.number}, ${plural(r.daysPastDue, 'day')} late${r.onHold ? ', on hold' : ''}` },
            ],
            end: { figure: money(r.balanceCents), ...(r.daysPastDue > 30 ? { tone: 'bad' as const } : {}) },
          }))}
        />
      )}
      {chat && (
        <div className="fl-aging__acts">
          <ViewAct surface="chat" onPress={() => host.open({ kind: 'owed' })}>
            Open in Flowology
          </ViewAct>
        </div>
      )}
    </article>
  );
}
